import { DateTime, Interval } from 'luxon'

const MINUTES_IN_HOUR = 60
const SECONDS_IN_MINUTE = MINUTES_IN_HOUR

const FORMAT_LOCALE = 'en-US'
/** Fallback when callers omit an IANA zone — keeps SSR and browser text identical. */
const DEFAULT_ZONE = 'UTC'

const resolveZone = (timezone) => timezone || DEFAULT_ZONE

const parseISO = (dateTime, timezone) => DateTime.fromISO(
  dateTime,
  timezone ? { zone: timezone, setZone: true } : undefined,
).setLocale(FORMAT_LOCALE)

export const getTimePeriod = (startDateTime, endDateTime) => `${DateTime.fromISO(startDateTime).setLocale(FORMAT_LOCALE).toFormat('h:mm')} - ${DateTime.fromISO(endDateTime).setLocale(FORMAT_LOCALE).toFormat('t')}`
export const getTimePeriodWithTimezone = (startDateTime, endDateTime, timezone) => {
  const zone = resolveZone(timezone)
  const start = parseISO(startDateTime, zone).setZone(zone)
  const end = parseISO(endDateTime, zone).setZone(zone)

  return `${start.toFormat('h:mm a')} - ${end.toFormat('t (ZZZZ)')}`
}

export const formattedTime = (startDateTime, endDateTime) => {
  const period = Interval.fromISO(`${startDateTime}/${endDateTime}`)
  const periodStart = period.start?.setLocale(FORMAT_LOCALE)
  const periodEnd = period.end?.setLocale(FORMAT_LOCALE)

  return (
    <time dateTime={period.toISO()} suppressHydrationWarning>
      {
        period.hasSame('day')
          ? `${periodStart.toFormat('EEE, MMM dd \u2022 h:mm')} - ${periodEnd.toFormat('t')}`
          : `${periodStart.toFormat('EEE, MMM dd')} - ${periodEnd.toFormat('EEE, MMM dd')}`
      }
    </time>
  )
}

export const formattedTimeWithTimezone = (startDateTime, endDateTime, timezone) => {
  const zone = resolveZone(timezone)
  const periodStart = parseISO(startDateTime, zone).setZone(zone)
  const periodEnd = parseISO(endDateTime, zone).setZone(zone)
  const period = Interval.fromDateTimes(periodStart, periodEnd)
  const isSameDay = periodStart.hasSame(periodEnd, 'day')

  return (
    <time dateTime={period.toISO()} suppressHydrationWarning>
      {
        isSameDay
          ? `${periodStart.toFormat('EEE, MMM dd \u2022 h:mm')} - ${periodEnd.toFormat('t (ZZZZ)')}`
          : `${periodStart.toFormat('EEE, MMM dd')} - ${periodEnd.toFormat('EEE, MMM dd')}`
      }
    </time>
  )
}

export const fullDayDateAndTime = (startDateTime, endDateTime) => `${DateTime.fromISO(startDateTime).setLocale(FORMAT_LOCALE).toFormat('ccc, LLL d')} • ${getTimePeriod(startDateTime, endDateTime)}`
export const fullDayDateAndTimeWithTimezone = (startDateTime, endDateTime, timezone) => {
  const zone = resolveZone(timezone)
  return `${parseISO(startDateTime, zone).setZone(zone).toFormat('ccc, LLL d')} • ${getTimePeriodWithTimezone(startDateTime, endDateTime, zone)}`
}

/** Group-event multi-session list rows (book card accordion). */
export const formatMultiSessionListLine = (startDateTime, endDateTime, timezone) => {
  const zone = resolveZone(timezone)
  const start = parseISO(startDateTime, zone).setZone(zone)
  const end = parseISO(endDateTime, zone).setZone(zone)
  const datePart = start.toFormat('ccc, LLL d')
  const timePart = `${start.toFormat('h:mm')} – ${end.toFormat('h:mm a (ZZZZ)')}`
  return `${datePart} · ${timePart}`
}
export const fullDayInstantWithTimezone = (dateTime, timezone) => {
  const zone = resolveZone(timezone)
  const dt = parseISO(dateTime, zone).setZone(zone)
  return `${dt.toFormat('ccc, LLL d')} • ${dt.toFormat('h:mm a (ZZZZ)')}`
}

// Format helpers. Prefer passing an IANA `timezone` for any SSR/hydrated UI.
// When omitted, getDate/getTime keep prior local-zone behavior (client-only safe).
// getShortDate / *WithTimezone always resolve through UTC so missing zones cannot
// diverge between Node SSR and the browser (React #418 text mismatches).
export const getDate = (startDateTime, timezone) => {
  const dt = parseISO(startDateTime, timezone)
  return `${(timezone ? dt.setZone(timezone) : dt).toFormat('cccc, LLLL d')}`
}
export const getTime = (startDateTime, timezone) => {
  const dt = parseISO(startDateTime, timezone)
  return `${(timezone ? dt.setZone(timezone) : dt).toFormat('h:mm a')}`
}
export const getShortDate = (startDateTime, timezone) => {
  const zone = resolveZone(timezone)
  return `${parseISO(startDateTime, zone).setZone(zone).toFormat('ccc, LLL d')}`
}
export const getTimeWithTimeZone = (startDateTime, endDateTime, timezone) => `${getTimePeriodWithTimezone(startDateTime, endDateTime, timezone)}`

export const getDeltaString = (minutesAgo, updatedDate) => {
  let date = updatedDate

  const dateOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }
  const dateFormatted = date.toLocaleDateString('en-US', dateOptions)

  if (minutesAgo < MINUTES_IN_HOUR) date = `${minutesAgo} minutes ago`
  if (minutesAgo <= 1) date = '1 minute ago'
  if (minutesAgo >= MINUTES_IN_HOUR) date = dateFormatted

  return date
}

export const diffInMinutes = (oldDate) => {
  return Math.ceil((Date.now() - new Date(oldDate).getTime()) / (SECONDS_IN_MINUTE * 1000))
}
